import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ExternalLayout } from "@/components/ExternalLayout";
import { StatusPill } from "@/components/StatusPill";
import { CUSTOMER_PHASES, CUSTOMER_PHASE_LABELS, fmtDate } from "@/lib/constants";
import { ArrowLeft, FileCheck, FileSignature, Eye, AlertTriangle, X } from "lucide-react";
import { toast } from "sonner";

export default function CustomerProject() {
  const { projectId } = useParams();
  const { user, isInternal } = useAuth();
  const previewMode = isInternal();
  const isEntscheider = (user?.roles || []).includes("kunde_entscheider");

  const [project, setProject] = useState(null);
  const [deliverables, setDeliverables] = useState([]);
  const [approvals, setApprovals] = useState([]);

  const load = async () => {
    const [p, d, a] = await Promise.all([
      api.get(`/projects/${projectId}`),
      api.get(`/projects/${projectId}/deliverables`).catch(() => ({ data: [] })),
      api.get(`/projects/${projectId}/approvals`).catch(() => ({ data: [] })),
    ]);
    setProject(p.data);
    setDeliverables(d.data);
    setApprovals(a.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  if (!project) {
    return (
      <ExternalLayout>
        <div className="font-mono text-xs text-neutral-500">Lade …</div>
      </ExternalLayout>
    );
  }

  const currentPhase = project.customer_phase;
  const idx = CUSTOMER_PHASES.indexOf(currentPhase);
  const so = project.sign_off_summary;

  return (
    <ExternalLayout>
      {previewMode && (
        <div
          data-testid="customer-preview-banner"
          className="mb-6 border border-amber-300 bg-amber-50 rounded-md px-4 py-3 flex items-start gap-3"
        >
          <Eye className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-amber-900">
            <div className="font-medium">Vorschau · Kundensicht</div>
            <div className="mt-0.5">
              Sie sehen das Kundenportal als interne Hospitanz. Aktionen sind in dieser Ansicht nicht möglich.
            </div>
          </div>
        </div>
      )}

      <Link to="/portal" className="inline-flex items-center gap-1.5 text-xs text-neutral-600 hover:text-[#183153] mb-6" data-testid="customer-back-link">
        <ArrowLeft className="w-3.5 h-3.5" /> Übersicht
      </Link>

      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-1">{project.customer_org}</div>
      <h1 className="font-display text-3xl md:text-4xl text-neutral-900 mb-3" data-testid="customer-project-title">{project.name}</h1>
      <StatusPill kind="customer_phase" value={currentPhase} />

      {project.description && (
        <p className="mt-6 text-sm text-neutral-700 leading-relaxed max-w-prose" data-testid="customer-description">
          {project.description}
        </p>
      )}

      {/* Phasenleiste */}
      <section className="mt-8" data-testid="customer-phase-list">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-3">Stand</div>
        <div className="border border-neutral-200 rounded-md overflow-hidden">
          {CUSTOMER_PHASES.map((p, i) => {
            const past = i < idx;
            const active = i === idx;
            return (
              <div
                key={p}
                data-testid={`customer-phase-${p}`}
                className={`flex items-start gap-4 px-5 py-4 border-b border-neutral-100 last:border-b-0 ${active ? "bg-neutral-50" : ""}`}
              >
                <span
                  className="inline-flex items-center justify-center w-7 h-7 font-mono text-xs flex-shrink-0"
                  style={{
                    background: active ? "#183153" : past ? "#171717" : "#FAFAFA",
                    color: active || past ? "#FFF" : "#A3A3A3",
                    border: active ? "none" : past ? "none" : "1px solid #E5E5E5",
                  }}
                >
                  {past ? "✓" : i + 1}
                </span>
                <div className="flex-1">
                  <div className={`font-display text-base ${active ? "text-neutral-900" : past ? "text-neutral-700" : "text-neutral-400"}`}>
                    {CUSTOMER_PHASE_LABELS[p]}
                  </div>
                  {active && <div className="text-xs text-neutral-600 mt-1">Aktuell</div>}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Vertrauensanker / Freigabe */}
      <section className="mt-10">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-3">Freigabe</div>
        {so ? (
          <div
            className="border border-emerald-300 bg-emerald-50 rounded-md p-5 flex items-start gap-3"
            data-testid="customer-signoff-anchor"
          >
            <FileSignature className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <div className="text-sm font-medium text-emerald-900">Projekt freigegeben</div>
              <div className="text-sm text-emerald-900 mt-1">
                am <span className="font-mono">{fmtDate(so.signed_off_at)}</span> durch <strong>{so.signed_off_by_name}</strong>
              </div>
              {so.sign_off_statement && (
                <div className="mt-3 text-sm text-emerald-900 leading-relaxed border-t border-emerald-200 pt-3" data-testid="customer-signoff-statement">
                  „{so.sign_off_statement}"
                </div>
              )}
            </div>
          </div>
        ) : (
          <div
            className="border border-neutral-200 rounded-md p-6 text-center text-sm text-neutral-500"
            data-testid="customer-signoff-pending"
          >
            Noch keine Freigabe erteilt.
          </div>
        )}
      </section>

      {/* Freigaben (Approvals) */}
      <CustomerApprovalsSection
        projectId={projectId}
        approvals={approvals}
        canDecide={isEntscheider && !previewMode}
        previewMode={previewMode}
        onChanged={load}
      />

      {/* Lieferobjekte */}
      <section className="mt-10">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-3">Lieferobjekte</div>
        {deliverables.length === 0 ? (
          <div
            className="border border-neutral-200 rounded-md p-6 text-center text-sm text-neutral-500"
            data-testid="customer-deliverables-empty"
          >
            Aktuell sind keine Lieferobjekte verfügbar.
          </div>
        ) : (
          <div className="border border-neutral-200 rounded-md divide-y divide-neutral-100" data-testid="customer-deliverables-list">
            {deliverables.map((d) => (
              <div key={d.deliv_id} className="px-5 py-4 flex items-center gap-4" data-testid={`customer-deliverable-${d.deliv_id}`}>
                <FileCheck className="w-5 h-5 text-neutral-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-neutral-900 truncate">{d.name}</div>
                  <div className="font-mono text-[10px] text-neutral-500">
                    {d.type} · v{d.version} · verfügbar seit {fmtDate(d.created_at)}
                  </div>
                </div>
                {d.url ? (
                  <a
                    href={d.url}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => previewMode && e.preventDefault()}
                    className={`text-xs px-3 py-1.5 border rounded-sm ${
                      previewMode
                        ? "border-neutral-200 text-neutral-400 cursor-not-allowed"
                        : "border-neutral-300 hover:bg-neutral-50 text-neutral-900"
                    }`}
                    data-testid={`customer-deliverable-link-${d.deliv_id}`}
                  >
                    Öffnen
                  </a>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </section>

      <div className="mt-12 p-5 border border-neutral-200 rounded-md bg-neutral-50">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-2">Kontakt</div>
        <p className="text-sm text-neutral-700 leading-relaxed">
          Fragen? Ihr AppWerkstatt-Team meldet sich bei jedem Übergabe-Schritt direkt bei Ihnen.
        </p>
      </div>
    </ExternalLayout>
  );
}


const STATUS_PILL = {
  offen: { bg: "#DBEAFE", text: "#1E40AF", border: "#93C5FD", label: "Ihre Entscheidung gefragt" },
  entschieden: { bg: "#DCFCE7", text: "#166534", border: "#86EFAC", label: "Entschieden" },
  verfallen: { bg: "#F4F4F5", text: "#525252", border: "#D4D4D8", label: "Frist abgelaufen" },
  zurueckgezogen: { bg: "#FEF2F2", text: "#7F1D1D", border: "#FECACA", label: "Zurückgezogen durch das Team" },
};

const CustomerApprovalsSection = ({ projectId, approvals, canDecide, previewMode, onChanged }) => {
  const [openId, setOpenId] = useState(null);
  const [confirmStep, setConfirmStep] = useState(null); // { decision: 'zustimmung'|'ablehnung' }
  const [comment, setComment] = useState("");

  const open = approvals.find((a) => a.approval_id === openId);

  // Sortierung: offen zuerst (nach Frist), dann andere
  const sorted = [...approvals].sort((a, b) => {
    if (a.status === "offen" && b.status !== "offen") return -1;
    if (b.status === "offen" && a.status !== "offen") return 1;
    if (a.status === "offen" && b.status === "offen") return (a.due_at || "").localeCompare(b.due_at || "");
    return (b.decided_at || b.expired_at || b.withdrawn_at || "").localeCompare(a.decided_at || a.expired_at || a.withdrawn_at || "");
  });

  const submitDecision = async () => {
    if (!confirmStep || !open) return;
    if (confirmStep.decision === "ablehnung" && comment.trim().length < 10) return;
    try {
      await api.post(`/projects/${projectId}/approvals/${open.approval_id}/decide`, {
        decision: confirmStep.decision,
        decision_comment: comment,
      });
      toast.success(confirmStep.decision === "zustimmung" ? "Zustimmung dokumentiert" : "Ablehnung dokumentiert");
      setConfirmStep(null);
      setOpenId(null);
      setComment("");
      onChanged();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Aktion fehlgeschlagen");
    }
  };

  return (
    <section className="mt-10" data-testid="customer-approvals-section">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-3">Freigaben</div>
      {sorted.length === 0 ? (
        <div className="border border-neutral-200 rounded-md p-6 text-center text-sm text-neutral-500" data-testid="customer-approvals-empty">
          Aktuell sind keine Freigaben offen.
        </div>
      ) : (
        <div className="border border-neutral-200 rounded-md divide-y divide-neutral-100">
          {sorted.map((a) => {
            const s = STATUS_PILL[a.status] || STATUS_PILL.offen;
            return (
              <button
                key={a.approval_id}
                onClick={() => { setOpenId(a.approval_id); setComment(""); setConfirmStep(null); }}
                data-testid={`customer-approval-${a.approval_id}`}
                className="w-full text-left px-5 py-4 hover:bg-neutral-50 flex items-start justify-between gap-3"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-neutral-900">{a.title}</div>
                  <div className="text-xs text-neutral-600 mt-0.5">
                    {a.status === "offen" && <>Frist: <span className="font-mono">{fmtDate(a.due_at)}</span></>}
                    {a.status === "entschieden" && <>{a.decision === "zustimmung" ? "Zugestimmt" : "Abgelehnt"} am <span className="font-mono">{fmtDate(a.decided_at)}</span></>}
                    {a.status === "verfallen" && <>Frist abgelaufen am <span className="font-mono">{fmtDate(a.expired_at)}</span></>}
                    {a.status === "zurueckgezogen" && <>Am <span className="font-mono">{fmtDate(a.withdrawn_at)}</span></>}
                  </div>
                </div>
                <span
                  className="inline-flex items-center px-2 py-0.5 text-[11px] border rounded-full flex-shrink-0"
                  style={{ background: s.bg, color: s.text, borderColor: s.border }}
                >
                  {s.label}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {open && !confirmStep && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setOpenId(null)}>
          <div className="bg-white border border-neutral-200 rounded-md max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="customer-approval-detail">
            <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between">
              <span className="font-display text-base">{open.title}</span>
              <button onClick={() => setOpenId(null)} className="text-neutral-400 hover:text-neutral-700"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-4 text-sm">
              <div className="text-neutral-700 whitespace-pre-wrap leading-relaxed">{open.context_text}</div>
              {open.target_label && (
                <div className="text-xs text-neutral-500">
                  <span className="font-mono uppercase tracking-widest text-[10px]">Bezug:</span> {open.target_label}
                </div>
              )}
              {open.status === "offen" && (
                <div className="text-xs text-neutral-700 bg-neutral-50 border border-neutral-200 rounded-sm px-3 py-2">
                  Frist: <span className="font-mono">{fmtDate(open.due_at)}</span>
                </div>
              )}
              {open.status === "entschieden" && (
                <div className={`text-sm rounded-sm px-3 py-2 border ${open.decision === "zustimmung" ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-red-50 border-red-200 text-red-900"}`}>
                  <div className="font-medium">{open.decision === "zustimmung" ? "Zugestimmt" : "Abgelehnt"}</div>
                  <div className="text-xs mt-1">am {fmtDate(open.decided_at)} durch {open.decided_by_name}</div>
                  {open.decision_comment && <div className="text-xs mt-2 italic">„{open.decision_comment}"</div>}
                </div>
              )}
              {open.status === "verfallen" && (
                <div className="text-sm bg-neutral-50 border border-neutral-200 rounded-sm px-3 py-2 text-neutral-700">
                  Diese Anfrage ist nicht mehr offen — die Frist ist am {fmtDate(open.expired_at)} verstrichen.
                </div>
              )}
              {open.status === "zurueckgezogen" && (
                <div className="text-sm bg-neutral-50 border border-neutral-200 rounded-sm px-3 py-2 text-neutral-700">
                  Diese Anfrage wurde durch das Team zurückgezogen.
                </div>
              )}
              {open.status === "offen" && (
                <div className="pt-2 border-t border-neutral-100 space-y-2">
                  {previewMode && (
                    <div className="text-[11px] text-amber-900 bg-amber-50 border border-amber-200 px-3 py-2 flex items-start gap-2">
                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                      Vorschau-Modus: Entscheidungen sind hier nicht möglich.
                    </div>
                  )}
                  {!canDecide && !previewMode && (
                    <div className="text-[11px] text-neutral-600">Diese Anfrage kann nur durch eine Person mit Entscheidungsbefugnis bearbeitet werden.</div>
                  )}
                  <div className="flex gap-2 justify-end">
                    <button
                      onClick={() => setConfirmStep({ decision: "ablehnung" })}
                      disabled={!canDecide}
                      data-testid="customer-approval-reject-button"
                      className="px-4 py-2 text-sm border border-red-300 text-red-700 rounded-sm disabled:opacity-50 hover:bg-red-50"
                    >
                      Ablehnen
                    </button>
                    <button
                      onClick={() => setConfirmStep({ decision: "zustimmung" })}
                      disabled={!canDecide}
                      data-testid="customer-approval-approve-button"
                      className="px-4 py-2 text-sm text-white rounded-sm disabled:opacity-50"
                      style={{ background: "#166534" }}
                    >
                      Zustimmen
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bestätigungs-Modal (zweite Stufe) */}
      {open && confirmStep && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={() => setConfirmStep(null)}>
          <div className="bg-white border border-neutral-200 rounded-md max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="customer-approval-confirm-modal">
            <div className="px-5 py-3 border-b border-neutral-200 font-display-medium">
              {confirmStep.decision === "zustimmung" ? "Zustimmung bestätigen" : "Ablehnung bestätigen"}
            </div>
            <div className="p-5 space-y-3 text-sm">
              <div className="text-xs bg-amber-50 border border-amber-200 px-3 py-2 text-amber-900 flex items-start gap-2">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                Ihre Entscheidung ist <strong>verbindlich und unwiderruflich</strong>.
              </div>
              <div className="text-neutral-700">
                {confirmStep.decision === "zustimmung"
                  ? <>Sie stimmen <strong>„{open.title}"</strong> zu.</>
                  : <>Sie lehnen <strong>„{open.title}"</strong> ab.</>}
              </div>
              {confirmStep.decision === "ablehnung" ? (
                <div>
                  <label className="text-[11px] font-mono uppercase tracking-widest text-neutral-500">
                    Begründung (Pflicht, mind. 10 Zeichen) <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    required
                    rows={3}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    data-testid="customer-approval-reject-comment"
                    className="w-full mt-1 px-3 py-2 border border-neutral-300 rounded-sm text-sm"
                  />
                </div>
              ) : (
                <div>
                  <label className="text-[11px] font-mono uppercase tracking-widest text-neutral-500">Optionaler Kommentar</label>
                  <textarea
                    rows={2}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    data-testid="customer-approval-approve-comment"
                    className="w-full mt-1 px-3 py-2 border border-neutral-300 rounded-sm text-sm"
                  />
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setConfirmStep(null)} className="px-4 py-2 text-sm border border-neutral-300 rounded-sm hover:bg-neutral-50">Zurück</button>
                <button
                  onClick={submitDecision}
                  disabled={confirmStep.decision === "ablehnung" && comment.trim().length < 10}
                  data-testid="customer-approval-confirm-button"
                  className="px-4 py-2 text-sm text-white rounded-sm disabled:opacity-50"
                  style={{ background: confirmStep.decision === "zustimmung" ? "#166534" : "#7F1D1D" }}
                >
                  {confirmStep.decision === "zustimmung" ? "Endgültig zustimmen" : "Endgültig ablehnen"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
